import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { get, post } from '../../utils/api';
import { isInternalNoEmail, sanitizeEmail } from '../../utils/customerEmail';

const COMMON_MISSPELLINGS = [
  { bad: /\bthnaks\b/gi, fix: 'thanks' },
  { bad: /\bteh\b/gi, fix: 'the' },
  { bad: /\byoru\b/gi, fix: 'your' },
  { bad: /\brecieve\b/gi, fix: 'receive' },
  { bad: /\bsponser\b/gi, fix: 'sponsor' },
];

function toTitleCase(text) {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .map((w) => w ? `${w[0].toUpperCase()}${w.slice(1)}` : '')
    .join(' ');
}

const normalizeTemplateKey = (raw) =>
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const EMAIL_TYPE_OPTIONS_BY_REPORT = {
  sponsors: [
    { value: 'thank_you', label: 'Thank You Email' },
    { value: 'sponsorship_request', label: 'Sponsorship Request' },
    { value: 'payment_reminder', label: 'Payment Reminder' },
    { value: 'special_instructions', label: 'Special Instructions' },
    { value: 'blank', label: 'Blank Email' },
  ],
  event: [
    { value: 'thank_you', label: 'Thank You Email' },
    { value: 'event_reminder', label: 'Event Reminder' },
    { value: 'special_instructions', label: 'Special Instructions' },
    { value: 'blank', label: 'Blank Email' },
  ],
  pledges: [
    { value: 'pledge_thank_you', label: 'Pledge Thank You' },
    { value: 'pledge_payment_reminder', label: 'Pledge Payment Reminder' },
    { value: 'pledge_balance_due', label: 'Pledge Balance Due' },
    { value: 'pledge_follow_up', label: 'Pledge Follow Up' },
    { value: 'blank', label: 'Blank Email' },
  ],
};

const DEFAULT_SEND_MODE_BY_EMAIL_TYPE = {
  pledge_thank_you: 'individual',
  pledge_payment_reminder: 'individual',
  pledge_balance_due: 'individual',
  pledge_follow_up: 'individual',
};

const normalizeSendMode = (raw, fallback = 'bcc') => {
  const v = String(raw || '').trim().toLowerCase();
  if (v === 'individual' || v === 'bcc') return v;
  return fallback;
};

const getEmailTypeOptions = (reportType) => {
  const key = String(reportType || '').trim().toLowerCase();
  return EMAIL_TYPE_OPTIONS_BY_REPORT[key] || EMAIL_TYPE_OPTIONS_BY_REPORT.sponsors;
};

const STANDARD_FOOTER =
  `Warm regards,\n\n` +
  `India Cultural Center and Temple (ICCT)\n` +
  `12005 Highway 64, Eads, TN 38028\n` +
  `Phone: (901) 202-6070  Email: icct@icctmemphis.org\n` +
  `Contact Us: https://icctmemphis.org/information/contact-us/`;

const formatCurrency = (v) => {
  if (typeof v === 'string' && /\{\{[^}]+\}\}/.test(v)) return v;
  const n = Number(v || 0);
  if (!isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const escapeRegExp = (v) => String(v || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const fillTemplate = (text, values) => {
  let out = String(text || '');
  Object.entries(values || {}).forEach(([key, value]) => {
    const tokenKey = String(key || '').trim().toLowerCase();
    if (!tokenKey) return;
    const tokenVariants = Array.from(new Set([
      tokenKey,
      tokenKey.replace(/_/g, '-'),
      tokenKey.replace(/-/g, '_'),
    ]));
    for (const tk of tokenVariants) {
      // Support both {{product_name}} and legacy {product-name} style placeholders.
      const doubleCurly = new RegExp(`{{\\s*${escapeRegExp(tk)}\\s*}}`, 'gi');
      const singleCurly = new RegExp(`\\{\\s*${escapeRegExp(tk)}\\s*\\}`, 'gi');
      const replacement = String(value == null ? '' : value);
      out = out.replace(doubleCurly, replacement);
      // Avoid triple-brace artifacts when replacement itself is a template token.
      if (!/[{}]/.test(replacement)) {
        out = out.replace(singleCurly, replacement);
      }
    }
  });
  return out;
};

function getPersonalizationSeedContext(context = {}) {
  return {
    ...(context || {}),
    recipientName: '{{recipient_name}}',
    productName: '{{product_name}}',
    eventName: '{{event_name}}',
    variationName: '{{variation_name}}',
    pledgeId: '{{pledge_id}}',
    sponsorshipAmount: '{{sponsorship_amount}}',
    pledgedAmount: '{{pledge_amount}}',
    paymentsMade: '{{payments_made}}',
    balanceAmount: '{{balance_amount}}',
    paymentLink: '{{payment_link}}',
  };
}

function buildTemplateTokens(reportType, context = {}) {
  const eventName = toTitleCase(context.reportTitle || context.productName || 'our event');
  const productName = toTitleCase(context.productName || context.reportTitle || 'our initiative');
  const pledgeId = context.pledgeId || context.pledge_id || '';
  return {
    recipient_name: context.recipientName || 'Sponsor',
    product_name: productName,
    event_name: eventName,
    pledge_id: pledgeId ? String(pledgeId) : '',
    variation_name: context.variationName || '',
    pledge_amount: formatCurrency(context.pledgedAmount || context.sponsorshipAmount || 0),
    sponsorship_amount: formatCurrency(context.sponsorshipAmount || 0),
    payments_made: formatCurrency(context.paymentsMade || 0),
    balance_amount: formatCurrency(context.balanceAmount || 0),
    payment_link: context.paymentLink || '',
    report_type: String(reportType || ''),
    date_range_label: context.dateRangeLabel || '',
  };
}

const SPELLING_DICTIONARY = new Set([
  'a', 'about', 'add', 'all', 'and', 'any', 'appreciate', 'are', 'as', 'at', 'be', 'best', 'body',
  'by', 'can', 'center', 'community', 'contact', 'continued', 'contribution', 'cultural', 'date',
  'dear', 'directly', 'eads', 'email', 'event', 'for', 'friendly', 'from', 'generous', 'grateful',
  'help', 'helps', 'highway', 'icct', 'if', 'in', 'india', 'information', 'initiative', 'instructions',
  'invite', 'is', 'it', 'like', 'line', 'mahadeva', 'of', 'on', 'one', 'opportunity', 'or', 'our',
  'phone', 'please', 'programs', 'regards', 'related', 'reminder', 'reply', 'review', 'send', 'services',
  'sincerely', 'special', 'sponsor', 'sponsoring', 'sponsorship', 'subject', 'support', 'supporting',
  'team', 'temple', 'thank', 'thanks', 'the', 'this', 'tn', 'to', 'us', 'warm', 'we', 'welcome', 'with',
  'would', 'you', 'your',
]);

function levenshteinDistance(a, b, maxDistance) {
  if (a === b) return 0;
  const al = a.length;
  const bl = b.length;
  if (Math.abs(al - bl) > maxDistance) return maxDistance + 1;

  const prev = new Array(bl + 1);
  const curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j += 1) prev[j] = j;

  for (let i = 1; i <= al; i += 1) {
    curr[0] = i;
    let minInRow = curr[0];
    for (let j = 1; j <= bl; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost
      );
      if (curr[j] < minInRow) minInRow = curr[j];
    }
    if (minInRow > maxDistance) return maxDistance + 1;
    for (let j = 0; j <= bl; j += 1) prev[j] = curr[j];
  }

  return prev[bl];
}

function findPotentialTypos(text) {
  const warnings = [];
  const seen = new Set();
  const tokenRegex = /\b([A-Za-z']{4,})\b/g;
  let match;

  while ((match = tokenRegex.exec(String(text || ''))) !== null) {
    const original = match[1];
    if (/[A-Z]/.test(original)) continue; // likely names/proper nouns
    const token = original.toLowerCase().replace(/'s$/, '');
    if (!token || SPELLING_DICTIONARY.has(token)) continue;
    if (seen.has(token)) continue;
    seen.add(token);

    const maxDistance = token.length >= 9 ? 3 : token.length >= 6 ? 2 : 1;
    let bestWord = '';
    let bestDistance = maxDistance + 1;

    for (const candidate of SPELLING_DICTIONARY) {
      if (!candidate || candidate[0] !== token[0]) continue;
      const dist = levenshteinDistance(token, candidate, maxDistance);
      if (dist < bestDistance) {
        bestDistance = dist;
        bestWord = candidate;
      }
    }

    if (bestWord && bestDistance <= maxDistance) {
      warnings.push({
        message: `Possible spelling issue: "${original}" (did you mean "${bestWord}"?)`,
        original,
        suggestion: bestWord,
      });
      if (warnings.length >= 4) break;
    }
  }

  return warnings;
}

function getTemplate(reportType, emailType, context = {}) {
  const today = new Date().toLocaleDateString();
  const eventName = toTitleCase(context.reportTitle || context.productName || 'our event');
  const productName = toTitleCase(context.productName || context.reportTitle || 'our initiative');
  const tokens = buildTemplateTokens(reportType, context);

  if (emailType === 'blank') {
    return {
      subject: '',
      body: fillTemplate(`Dear {{recipient_name}},\n\n${STANDARD_FOOTER}`, tokens),
    };
  }
  if (emailType === 'pledge_payment_reminder') {
    return {
      subject: fillTemplate('Payment Reminder - Pledge #{{pledge_id}} - {{product_name}}', tokens),
      body: fillTemplate(
        `Dear {{recipient_name}},\n\n` +
        `This is a reminder for your pledge payment.\n\n` +
        `Pledge #{{pledge_id}}\n` +
        `Item: {{product_name}}\n` +
        `Pledged Amount: {{pledge_amount}}\n` +
        `Payments Made: {{payments_made}}\n` +
        `Balance Due: {{balance_amount}}\n\n` +
        `Payment Link: {{payment_link}}\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`, tokens),
    };
  }
  if (emailType === 'pledge_balance_due') {
    return {
      subject: fillTemplate('Balance Due - Pledge #{{pledge_id}}', tokens),
      body: fillTemplate(
        `Dear {{recipient_name}},\n\n` +
        `A balance remains on your pledge.\n\n` +
        `Pledge #{{pledge_id}}\n` +
        `Item: {{product_name}}\n` +
        `Pledged Amount: {{pledge_amount}}\n` +
        `Balance Due: {{balance_amount}}\n\n` +
        `Payment Link: {{payment_link}}\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`, tokens),
    };
  }
  if (emailType === 'pledge_follow_up') {
    return {
      subject: fillTemplate('Pledge Follow Up - {{product_name}}', tokens),
      body: fillTemplate(
        `Dear {{recipient_name}},\n\n` +
        `This is a follow up regarding your pledge.\n\n` +
        `Pledge #{{pledge_id}}\n` +
        `Item: {{product_name}}\n` +
        `Pledged Amount: {{pledge_amount}}\n` +
        `Payments Made: {{payments_made}}\n` +
        `Balance Due: {{balance_amount}}\n\n` +
        `Please contact us if you need any assistance.\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`, tokens),
    };
  }
  if (emailType === 'pledge_thank_you') {
    return {
      subject: fillTemplate('Thank you for your pledge - {{product_name}}', tokens),
      body: fillTemplate(
        `Dear {{recipient_name}},\n\n` +
        `Thank you for your pledge support.\n\n` +
        `Pledge #{{pledge_id}}\n` +
        `Item: {{product_name}}\n` +
        `Pledged Amount: {{pledge_amount}}\n` +
        `Payments Made: {{payments_made}}\n` +
        `Balance: {{balance_amount}}\n\n` +
        `We appreciate your continued support.\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`, tokens),
    };
  }

  if (emailType === 'event_reminder') {
    return {
      subject: `Reminder: ${eventName}`,
      body:
        `Dear Sponsor,\n\n` +
        `This is a friendly reminder about ${eventName}.\n\n` +
        `Thank you for your continued support.\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`,
    };
  }

  if (emailType === 'sponsorship_request') {
    return {
      subject: `Sponsorship Opportunity - ${productName}`,
      body:
        `Dear Sponsor,\n\n` +
        `We invite you to support ${productName}. Your sponsorship helps ICCT continue temple and community programs.\n\n` +
        `Please contact us if you would like to sponsor.\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`,
    };
  }

  if (emailType === 'special_instructions') {
    return {
      subject: `Special Instructions - ${productName}`,
      body:
        `Dear Sponsor,\n\n` +
        `Please review the following instructions related to ${productName}:\n` +
        `1. [Add instruction]\n` +
        `2. [Add instruction]\n\n` +
        `If you have any questions, please reply to this email.\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`,
    };
  }

  if (emailType === 'payment_reminder') {
    const subject = 'Payment Reminder - {{product_name}}';
    const body =
      `Dear {{recipient_name}},\n\n` +
      `This is a gentle reminder regarding your sponsorship payment.\n\n` +
      `Sponsorship Amount: {{sponsorship_amount}}\n` +
      `Payments Made: {{payments_made}}\n` +
      `Balance Due: {{balance_amount}}\n\n` +
      `Payment Link: {{payment_link}}\n\n` +
      `${STANDARD_FOOTER}\n` +
      `Date: ${today}`;
    return { subject: fillTemplate(subject, tokens), body: fillTemplate(body, tokens) };
  }

  // default: thank_you
  if (reportType === 'event') {
    return {
      subject: fillTemplate(`Thank you for supporting {{event_name}}`, tokens),
      body: fillTemplate(
        `Dear Sponsor,\n\n` +
        `Thank you for your generous support for {{event_name}}. Your contribution helps ICCT serve the community and organize meaningful programs.\n\n` +
        `We sincerely appreciate your support.\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`, tokens),
    };
  }
  if (reportType === 'pledges') {
    return {
      subject: fillTemplate(`Thank you for your pledge - {{product_name}}`, tokens),
      body: fillTemplate(
        `Dear {{recipient_name}},\n\n` +
        `Thank you for supporting {{product_name}} through your pledge.\n\n` +
        `Pledge #{{pledge_id}}\n` +
        `Pledged Amount: {{pledge_amount}}\n` +
        `Payments Made: {{payments_made}}\n` +
        `Balance: {{balance_amount}}\n\n` +
        `${STANDARD_FOOTER}\n` +
        `Date: ${today}`, tokens),
    };
  }
  return {
    subject: fillTemplate(`Thank you for your sponsorship - {{product_name}}`, tokens),
    body: fillTemplate(
      `Dear {{recipient_name}},\n\n` +
      `Thank you for sponsoring {{product_name}}. Your support directly helps ICCT continue temple and community services.\n\n` +
      `We are grateful for your continued support.\n\n` +
      `${STANDARD_FOOTER}\n` +
      `Date: ${today}`, tokens),
  };
}

function getManagedTemplate(managedTemplateMap, reportType, emailType, context = {}) {
  const key = normalizeTemplateKey(emailType);
  const row = managedTemplateMap?.[key];
  if (!row) return null;
  const tokens = buildTemplateTokens(reportType, context);
  const subject = fillTemplate(row.subject_template || '', tokens);
  const body = fillTemplate(row.body_template || '', tokens);
  return { subject, body };
}

function reviewDraft(subject, body, emailType) {
  const warnings = [];
  const cleanSubject = String(subject || '').trim();
  const cleanBody = String(body || '').trim();

  if (!cleanSubject) warnings.push({ message: 'Subject is empty.' });
  if (!cleanBody) warnings.push({ message: 'Email body is empty.' });
  if (cleanSubject.length > 120) warnings.push({ message: 'Subject is long; consider keeping it under 120 characters.' });
  if (cleanBody.length < 30) warnings.push({ message: 'Body looks too short.' });
  if (emailType !== 'blank' && !/^((dear|hello|hi)\b)/i.test(cleanBody)) warnings.push({ message: 'Body should start with a greeting like "Dear Sponsor".' });
  if (emailType === 'thank_you' && !/(thank you|thanks)/i.test(cleanBody)) warnings.push({ message: 'Body should include a thank-you message.' });
  if (emailType !== 'blank' && !/(regards|sincerely|thanks|warm regards)/i.test(cleanBody)) warnings.push({ message: 'Body should include a closing line.' });
  if (/\s{3,}/.test(cleanBody)) warnings.push({ message: 'Body has repeated spaces.' });

  for (const misspelling of COMMON_MISSPELLINGS) {
    misspelling.bad.lastIndex = 0;
    const combined = `${cleanSubject}\n${cleanBody}`;
    const hit = misspelling.bad.exec(combined);
    if (hit && hit[0]) {
      warnings.push({
        message: `Possible typo detected. Consider "${misspelling.fix}".`,
        original: hit[0],
        suggestion: misspelling.fix,
      });
    }
  }
  const spellingWarnings = findPotentialTypos(`${cleanSubject}\n${cleanBody}`);
  for (const w of spellingWarnings) warnings.push(w);

  return warnings;
}

export default function ThankYouEmailDialog({
  open,
  onClose,
  reportType,
  context,
  recipients,
  recipientContexts = {},
  initialEmailType = 'thank_you',
  onSent,
}) {
  const emailTypeOptions = React.useMemo(() => getEmailTypeOptions(reportType), [reportType]);
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [emailType, setEmailType] = React.useState('thank_you');
  const [sending, setSending] = React.useState(false);
  const [reviewing, setReviewing] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [cloudWarnings, setCloudWarnings] = React.useState([]);
  const [sendResult, setSendResult] = React.useState(null);
  const [managedTemplates, setManagedTemplates] = React.useState({});
  const [loadingTemplates, setLoadingTemplates] = React.useState(false);

  const loadManagedTemplates = React.useCallback(async () => {
    const res = await get('/api/manage/email-templates', { include_inactive: 'false' });
    const rows = Array.isArray(res?.templates) ? res.templates : [];
    const next = {};
    for (const t of rows) {
      const key = normalizeTemplateKey(t?.template_key || '');
      if (!key) continue;
      next[key] = t;
    }
    setManagedTemplates(next);
    return next;
  }, []);

  const localWarnings = React.useMemo(() => reviewDraft(subject, body, emailType), [subject, body, emailType]);
  const warnings = React.useMemo(() => {
    const out = [];
    const seen = new Set();
    for (const item of [...localWarnings, ...cloudWarnings]) {
      const entry = typeof item === 'string' ? { message: item } : item;
      const key = String(entry?.message || '').trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(entry);
    }
    return out;
  }, [localWarnings, cloudWarnings]);
  const recipientStats = React.useMemo(() => {
    const set = new Set();
    let skippedInternal = 0;
    let skippedInvalid = 0;
    (Array.isArray(recipients) ? recipients : []).forEach((email) => {
      const val = sanitizeEmail(email);
      if (!val || !val.includes('@')) {
        skippedInvalid += 1;
        return;
      }
      if (isInternalNoEmail(val)) {
        skippedInternal += 1;
        return;
      }
      set.add(val);
    });
    return {
      recipients: Array.from(set),
      skippedInternal,
      skippedInvalid,
    };
  }, [recipients]);
  const uniqueRecipients = recipientStats.recipients;
  const normalizedRecipientContexts = React.useMemo(() => {
    const out = {};
    Object.entries(recipientContexts || {}).forEach(([email, ctx]) => {
      const key = sanitizeEmail(email);
      if (!key) return;
      out[key] = ctx || {};
    });
    return out;
  }, [recipientContexts]);
  const selectedTemplate = React.useMemo(() => {
    const key = normalizeTemplateKey(emailType);
    return managedTemplates?.[key] || null;
  }, [emailType, managedTemplates]);
  const deliveryMode = React.useMemo(() => {
    const fallback = DEFAULT_SEND_MODE_BY_EMAIL_TYPE[normalizeTemplateKey(emailType)] || 'bcc';
    return normalizeSendMode(selectedTemplate?.send_mode, fallback);
  }, [selectedTemplate, emailType]);
  const isIndividualMode = deliveryMode === 'individual';
  const personalizationSeedContext = React.useMemo(() => (
    isIndividualMode ? getPersonalizationSeedContext(context) : context
  ), [isIndividualMode, context]);

  React.useEffect(() => {
    if (!open) return;
    const startingType = String(initialEmailType || 'thank_you');
    setEmailType(startingType);
    const initialTpl = getTemplate(reportType, startingType, personalizationSeedContext);
    setSubject(initialTpl.subject);
    setBody(initialTpl.body);
    setCloudWarnings([]);
    setError('');
    setSuccess('');
    setSendResult(null);
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const loaded = await loadManagedTemplates();
        if (cancelled) return;
        const tpl = getManagedTemplate(loaded || {}, reportType, startingType, personalizationSeedContext)
          || getTemplate(reportType, startingType, personalizationSeedContext);
        setSubject(tpl.subject);
        setBody(tpl.body);
      } catch {
        // Keep fallback template when managed template fetch fails.
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, reportType, personalizationSeedContext, initialEmailType, loadManagedTemplates]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Subject and body are required.');
      return;
    }
    if (uniqueRecipients.length === 0) {
      setError('No recipient email addresses found in current selection.');
      return;
    }
    setSending(true);
    setError('');
    setSuccess('');
    setSendResult(null);
    try {
      const res = await post('/api/reports/send-email', {
        report_type: reportType,
        email_type: emailType,
        context: context || {},
        subject: subject.trim(),
        body: body.trim(),
        recipients: uniqueRecipients,
        recipient_contexts: normalizedRecipientContexts,
        delivery_mode: deliveryMode,
      });
      setSuccess(
        deliveryMode === 'individual'
          ? `Personalized emails sent to ${res?.recipient_count || uniqueRecipients.length} recipient(s).`
          : `Email sent to ICCT with ${res?.recipient_count || uniqueRecipients.length} recipient(s) in BCC.`
      );
      setSendResult({
        ok: true,
        toEmail: res?.to_email || 'icct@icctmemphis.org',
        recipientCount: Number(res?.recipient_count || uniqueRecipients.length),
        chunkCount: Number(res?.chunk_count || 1),
        deliveryMode,
        emailType,
      });
      if (typeof onSent === 'function') onSent(res);
    } catch (e) {
      const msg = e?.message || 'Failed to send email';
      setError(msg);
      setSendResult({
        ok: false,
        message: msg,
      });
    } finally {
      setSending(false);
    }
  };

  const handleCloudReview = async () => {
    if (!subject.trim() && !body.trim()) {
      setError('Enter subject or body before running cloud review.');
      return;
    }
    setReviewing(true);
    setError('');
    try {
      const res = await post('/api/reports/review-email', {
        report_type: reportType,
        email_type: emailType,
        subject: subject.trim(),
        body: body.trim(),
      });
      const cloud = Array.isArray(res?.cloud_warnings)
        ? res.cloud_warnings.map((w) => ({ message: String(w || '') }))
        : [];
      setCloudWarnings(cloud);
      if (cloud.length === 0) {
        setSuccess('Cloud review completed. No LanguageTool issues found.');
      } else {
        setSuccess('Cloud review completed. Please check warnings below.');
      }
    } catch (e) {
      setError(e?.message || 'Cloud review failed');
    } finally {
      setReviewing(false);
    }
  };

  const applySuggestion = (warning) => {
    const original = String(warning?.original || '').trim();
    const suggestion = String(warning?.suggestion || '').trim();
    if (!original || !suggestion) return;
    const pattern = new RegExp(`\\b${escapeRegExp(original)}\\b`, 'i');
    if (pattern.test(subject)) {
      setSubject((prev) => prev.replace(pattern, suggestion));
      return;
    }
    if (pattern.test(body)) {
      setBody((prev) => prev.replace(pattern, suggestion));
    }
  };
  const previewSubject = React.useMemo(() => {
    if (!isIndividualMode) return subject || '-';
    return String(subject || '-')
      .replace(/{{\s*[\w-]+\s*}}/g, '(personalized)')
      .replace(/\{\s*[\w-]+\s*\}/g, '(personalized)');
  }, [subject, isIndividualMode]);
  const previewBody = React.useMemo(() => {
    if (!isIndividualMode) return body || '-';
    return String(body || '-')
      .replace(/{{\s*[\w-]+\s*}}/g, '(personalized)')
      .replace(/\{\s*[\w-]+\s*\}/g, '(personalized)');
  }, [body, isIndividualMode]);

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>Send Email</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {isIndividualMode
              ? <>This sends <strong>one personalized email per recipient</strong> with <strong>To:</strong> icct@icctmemphis.org and recipient in BCC.</>
              : <>This sends one email with <strong>To:</strong> icct@icctmemphis.org and selected recipients in BCC.</>}
          </Typography>
          <Typography variant="body2">
            <strong>{isIndividualMode ? 'Recipients (personalized):' : 'Recipients (BCC):'}</strong> {uniqueRecipients.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Delivery mode: <strong>{isIndividualMode ? 'Individual' : 'BCC'}</strong>
          </Typography>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Email Type</InputLabel>
            <Select
              value={emailType}
              label="Email Type"
              onChange={(e) => {
                const nextType = e.target.value;
                setEmailType(nextType);
                const nextTpl = managedTemplates?.[normalizeTemplateKey(nextType)] || null;
                const nextMode = normalizeSendMode(
                  nextTpl?.send_mode,
                  DEFAULT_SEND_MODE_BY_EMAIL_TYPE[normalizeTemplateKey(nextType)] || 'bcc'
                );
                const nextSeedContext = nextMode === 'individual' ? getPersonalizationSeedContext(context) : context;
                const tpl = getManagedTemplate(managedTemplates, reportType, nextType, nextSeedContext)
                  || getTemplate(reportType, nextType, nextSeedContext);
                setSubject(tpl.subject);
                setBody(tpl.body);
                setCloudWarnings([]);
              }}
            >
              {emailTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(recipientStats.skippedInternal > 0 || recipientStats.skippedInvalid > 0) ? (
            <Alert severity="warning">
              {recipientStats.skippedInternal > 0 ? `${recipientStats.skippedInternal} recipient(s) skipped (internally assigned no-email). ` : ''}
              {recipientStats.skippedInvalid > 0 ? `${recipientStats.skippedInvalid} recipient(s) skipped (invalid email).` : ''}
            </Alert>
          ) : null}
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            size="small"
            fullWidth
            disabled={loadingTemplates}
          />
          <TextField
            label="Email Body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={8}
            fullWidth
            disabled={loadingTemplates}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              onClick={handleCloudReview}
              disabled={reviewing || sending}
            >
              {reviewing ? 'Reviewing...' : 'Run Cloud Review'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Uses LanguageTool public API for stronger grammar/spell suggestions.
            </Typography>
          </Stack>
          {warnings.length > 0 && !sendResult?.ok ? (
            <Alert severity="warning">
              <strong>Draft review warnings:</strong>
              <Box component="ul" sx={{ mb: 0, mt: 1, pl: 2 }}>
                {warnings.map((w, idx) => (
                  <li key={`warn-${idx}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography component="span" variant="body2">{w.message}</Typography>
                      {w?.original && w?.suggestion ? (
                        <Tooltip title={`Apply "${w.suggestion}"`}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => applySuggestion(w)}
                            sx={{ p: 0.25 }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Box>
                  </li>
                ))}
              </Box>
            </Alert>
          ) : (
            <Alert severity="success">Draft review passed basic grammar/spell checks.</Alert>
          )}
          <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Preview</Typography>
            {isIndividualMode ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Personalized placeholders are shown as <strong>(personalized)</strong> and rendered per recipient at send time.
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mb: 1 }}><strong>Subject:</strong> {previewSubject}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{previewBody}</Typography>
          </Box>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}
          {sendResult?.ok ? (
            <Alert severity="success">
              <strong>Email sent successfully.</strong>
              <Box component="div" sx={{ mt: 0.75 }}>
                <div>To: {sendResult.toEmail}</div>
                <div>{sendResult.deliveryMode === 'individual' ? 'Recipients:' : 'BCC recipients:'} {sendResult.recipientCount}</div>
                <div>Batches sent: {sendResult.chunkCount}</div>
                {sendResult.failedCount ? <div>Failed: {sendResult.failedCount}</div> : null}
              </Box>
            </Alert>
          ) : null}
          {sendResult && !sendResult.ok ? (
            <Alert severity="error">
              <strong>Email was not sent.</strong>
              <Box component="div" sx={{ mt: 0.75 }}>
                {sendResult.message || 'Unknown error'}
              </Box>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {sendResult ? (
          <Button onClick={onClose} variant="contained">Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={sending}>Cancel</Button>
            <Button variant="contained" onClick={handleSend} disabled={sending || uniqueRecipients.length === 0}>
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
